import React, { type PropsWithChildren } from "react";

import clsx from "clsx";
import { type Control, type UseFormRegister } from "react-hook-form";

import { Loading } from "@components/title-slide-view";
import {
  BenchmarkPlatformEnum,
  BenchmarkTypeEnum,
} from "@north-beam/nb-client-side";

import { PrettyBenchmarkAttributionModelNames } from "../constants";

import { type ChooseAndSetValidationTargets } from "./benchmark-choose-and-set-modal";
import {
  BenchmarkReviewCard,
  EditableBenchmarkReviewCard,
} from "./benchmark-review-card";
import {
  type BenchmarksByPlatform,
  type PlatformBenchmarks,
} from "./custom-benchmarks-modal";

const LoadingPlatformMetrics = () => {
  return (
    <div className="relative">
      <Loading />
      <BenchmarkReviewCard
        platform={BenchmarkPlatformEnum.adwords}
        cac={null}
        roas={null}
        label={null}
        loading={false}
        ignoreNotEnoughData
        benchmarkTarget={null}
        benchmarkType={BenchmarkTypeEnum.CAC}
      />
      <BenchmarkReviewCard
        platform={BenchmarkPlatformEnum.facebook}
        cac={null}
        roas={null}
        label={null}
        loading={false}
        ignoreNotEnoughData
        benchmarkTarget={null}
        benchmarkType={BenchmarkTypeEnum.CAC}
      />
      <BenchmarkReviewCard
        platform={BenchmarkPlatformEnum.youtube}
        cac={null}
        roas={null}
        label={null}
        loading={false}
        ignoreNotEnoughData
        benchmarkTarget={null}
        benchmarkType={BenchmarkTypeEnum.CAC}
      />
    </div>
  );
};

export const BenchmarkPlatformMetricsGridSkeleton = ({
  children,
  loading,
  notScrollable,
  compact = true,
}: PropsWithChildren<{
  loading?: boolean;
  notScrollable?: boolean;
  compact?: boolean;
}>) => {
  if (loading) {
    return <LoadingPlatformMetrics />;
  }

  return (
    <div
      className={clsx(
        "xl:w-[45rem] lg:w-[40rem] m:w-[35rem] relative flex-grow",
        compact ? "h-[11rem]" : "h-[18rem]",
        notScrollable ? "" : "overflow-y-auto",
      )}
    >
      {children}
    </div>
  );
};

export const BenchmarkPlatformMetricsGrid = ({
  orderedData,
  showLabels,
  loading,
  notScrollable,
  compact = true,
  benchmarkTargetsByPlatform,
  benchmarkType,
}: {
  orderedData: PlatformBenchmarks[];
  showLabels: boolean;
  loading?: boolean;
  notScrollable?: boolean;
  compact?: boolean;
  benchmarkType: BenchmarkTypeEnum;
  benchmarkTargetsByPlatform: ChooseAndSetValidationTargets;
}) => {
  return (
    <BenchmarkPlatformMetricsGridSkeleton
      notScrollable={notScrollable}
      compact={compact}
      loading={loading}
    >
      {orderedData.map(({ cac, roas, platform, attributionModel }) => {
        return (
          <BenchmarkReviewCard
            key={`${platform}-${attributionModel}`}
            platform={platform}
            cac={cac}
            roas={roas}
            label={
              showLabels
                ? PrettyBenchmarkAttributionModelNames[attributionModel]
                : null
            }
            loading={!!loading}
            benchmarkTarget={benchmarkTargetsByPlatform[platform]}
            benchmarkType={benchmarkType}
          />
        );
      })}
    </BenchmarkPlatformMetricsGridSkeleton>
  );
};

export const EditableBenchmarkPlatformMetricsGrid = ({
  orderedData,
  showLabels,
  loading,
  control,
  register,
  notScrollable,
  benchmarkTargetsByPlatform,
  benchmarkType,
}: {
  orderedData: PlatformBenchmarks[];
  showLabels: boolean;
  loading?: boolean;
  notScrollable?: boolean;
  control: Control<BenchmarksByPlatform>;
  register: UseFormRegister<BenchmarksByPlatform>;
  benchmarkType: BenchmarkTypeEnum;
  benchmarkTargetsByPlatform: ChooseAndSetValidationTargets;
}) => {
  return (
    <BenchmarkPlatformMetricsGridSkeleton
      notScrollable={notScrollable}
      loading={loading}
    >
      {orderedData.map(({ cac, roas, platform, attributionModel }, i) => {
        return (
          <EditableBenchmarkReviewCard
            key={`${platform}-${i}`}
            platform={platform}
            cac={cac}
            roas={roas}
            label={
              showLabels
                ? PrettyBenchmarkAttributionModelNames[attributionModel]
                : null
            }
            index={i}
            loading={!!loading}
            control={control}
            register={register}
            neverGreyOut
            ignoreNotEnoughData
            benchmarkTarget={benchmarkTargetsByPlatform[platform]}
            benchmarkType={benchmarkType}
          />
        );
      })}
    </BenchmarkPlatformMetricsGridSkeleton>
  );
};
